import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI } from '../services/api';
import { firmService } from '../services/firmService';
import { Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert, Button, Breadcrumbs, Link, Divider } from '@mui/material';

const Projects: React.FC = () => {
  const params = useParams<{ firmId?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState<any[]>([]);
  const [firmName, setFirmName] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await projectAPI.getAll(params.firmId ? { firm_id: params.firmId } : undefined);
        setProjects(res.data.data.data || res.data.data);
        if (params.firmId) {
          try {
            const firmRes = await firmService.getFirmById(parseInt(params.firmId));
            setFirmName(firmRes.data.name);
          } catch (_e) {
            setFirmName('');
          }
        } else {
          setFirmName('');
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [params.firmId]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  return (
    <Box>
      {params.firmId && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
            sx={{ textDecoration: 'none' }}
          >
            Firms
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${params.firmId}`); }}
            sx={{ textDecoration: 'none' }}
          >
            {firmName || `Firm #${params.firmId}`}
          </Link>
          <Typography color="text.primary">Projects</Typography>
        </Breadcrumbs>
      )}
      <Typography variant="h4" gutterBottom>
        {params.firmId ? 'Firm Projects' : 'Projects'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {params.firmId ? 'Projects scoped to the selected firm' : 'All projects'}
      </Typography>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Firm</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>#{p.id}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.firm?.name || p.firm_id}</TableCell>
                  <TableCell>
                    <Chip label={String(p.status).toUpperCase()} size="small" color={p.status === 'active' ? 'primary' : p.status === 'draft' ? 'default' : 'success'} />
                  </TableCell>
                  <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => navigate(`/firms/${p.firm_id || p.firm?.id || params.firmId}/projects/${p.id}`)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Projects; 